import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ShieldIcon,
  LockIcon,
  UserIcon,
  CheckIcon,
  XCircleIcon,
  Edit2Icon,
} from "../components/icons/FeatherIcons";
import { usePageTracking } from "@/hooks/usePageTracking";

const PrivacySecurityPage: React.FC = () => {
  usePageTracking("PrivacySecurity", "PrivacySecurityPage");
  const navigate = useNavigate();

  return (
    <div className="min-h-full bg-white">
      {/* Header */}
      <header className="bg-white px-4 py-3 flex items-center shadow-sm border-b border-gray-200 sticky-header">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Privacy & Security</h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-20 hide-scrollbar">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Introduction */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <ShieldIcon className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-lg font-bold text-gray-900">
                Your Privacy Matters
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              At Who's in?, we're committed to protecting your privacy and
              ensuring the security of your personal information. This page
              outlines how we collect, use, and protect your data.
            </p>
          </div>

          {/* Data Collection */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <UserIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-lg font-bold text-gray-900">
                Information We Collect
              </h2>
            </div>
            <div className="space-y-3 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Personal Information:
                </h3>
                <p>• Username, email address, profile photo, and location</p>
                <p>• Activity preferences and interests</p>
                <p>• Social connections within the app</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Usage Data:
                </h3>
                <p>• Activities you create, join, or interact with</p>
                <p>• App usage patterns and preferences</p>
                <p>• Device information and technical data</p>
              </div>
            </div>
          </div>

          {/* How We Use Data */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <CheckIcon className="w-6 h-6 text-green-600 mr-3" />
              <h2 className="text-lg font-bold text-gray-900">
                How We Use Your Data
              </h2>
            </div>
            <div className="space-y-2 text-gray-700">
              <p>• Provide and improve our services</p>
              <p>• Connect you with relevant activities and people</p>
              <p>
                • Send notifications about activities and social interactions
              </p>
              <p>• Ensure platform safety and prevent abuse</p>
              <p>• Analyze usage to enhance user experience</p>
            </div>
          </div>

          {/* Privacy Controls */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <Edit2Icon className="w-6 h-6 text-purple-600 mr-3" />
              <h2 className="text-lg font-bold text-gray-900">
                Your Privacy Controls
              </h2>
            </div>
            <div className="space-y-3 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Profile Visibility:
                </h3>
                <p>
                  Control who can see your profile and activities through your
                  account settings.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Activity Privacy:
                </h3>
                <p>
                  Choose whether your activities are public or visible only to
                  your connections.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Notification Preferences:
                </h3>
                <p>
                  Customize what notifications you receive and how you're
                  contacted.
                </p>
              </div>
            </div>
          </div>

          {/* Security Measures */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <LockIcon className="w-6 h-6 text-red-600 mr-3" />
              <h2 className="text-lg font-bold text-gray-900">
                Security Measures
              </h2>
            </div>
            <div className="space-y-2 text-gray-700">
              <p>• End-to-end encryption for sensitive communications</p>
              <p>• Secure data storage with industry-standard protocols</p>
              <p>• Regular security audits and updates</p>
              <p>• Two-factor authentication (coming soon)</p>
              <p>• Automatic logout from inactive sessions</p>
            </div>
          </div>

          {/* Data Sharing */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <XCircleIcon className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-lg font-bold text-gray-900">
                Data Sharing & Third Parties
              </h2>
            </div>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold text-gray-900">
                We do NOT sell your personal data.
              </p>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Limited sharing occurs only for:
                </h3>
                <p>• Essential service providers (hosting, analytics)</p>
                <p>• Legal compliance when required by law</p>
                <p>• Safety and security purposes</p>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <CheckIcon className="w-6 h-6 text-indigo-600 mr-3" />
              <h2 className="text-lg font-bold text-gray-900">Your Rights</h2>
            </div>
            <div className="space-y-2 text-gray-700">
              <p>• Access and download your personal data</p>
              <p>• Correct or update your information</p>
              <p>• Delete your account and associated data</p>
              <p>• Opt out of non-essential communications</p>
              <p>• Request data portability</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-primary/5 to-blue-500/5 rounded-lg p-6 border border-primary/20">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              Questions or Concerns?
            </h2>
            <div className="space-y-2 text-gray-700">
              <p>
                If you have questions about our privacy practices or want to
                exercise your rights:
              </p>
              <p className="font-semibold">Email: contact@whoisin.sg</p>
              <p className="text-sm text-gray-600 mt-3">
                This privacy policy was last updated on October 13, 2025.
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate("/settings")}
                className="flex items-center justify-center p-3 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
              >
                <CheckIcon className="w-5 h-5 mr-2" />
                Account Settings
              </button>
              <button
                onClick={() => navigate("/profile/edit")}
                className="flex items-center justify-center p-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Edit2Icon className="w-5 h-5 mr-2" />
                Privacy Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySecurityPage;
